import { useCallback, useEffect } from 'react';

import {
  Box,
  Calendar,
  DateInput,
  DatePicker,
  DropdownSelect,
  FormField,
  FormHelp,
  Label,
} from '@zillow/constellation-10';

import { flexContainerStyles, flexRowStyles } from '../../styles';

export const LEASE_TERM_OPTIONS = [
  { label: 'Fixed term', value: 'Fixed term' },
  {
    label: 'Month-to-month',
    value: 'Month-to-month',
  },
];

const FORM_LEASE_TERMS_CONTENT = {
  FORM_DATES_LABEL_MOVE_IN_DATE: 'Lease start',
  FORM_DATES_LABEL_MOVE_OUT_DATE: 'Lease end',
} as const;

const FORM_ERROR_MESSAGES = {
  DATE_REQUIRED: 'Date required',
  startDate: 'Start date must be before the end date',
  endDate: 'End date must be after the move in date',
  startDateTheSameAsEndDate: 'Start date and end date cannot be the same',
  endDateTheSameAsStartDate: 'End date and start date cannot be the same',
} as const;

const getMsFromDate = (date: string) => {
  return date ? new Date(date).getTime() : null;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
};

const minusDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);

  return result;
};

type FormFieldLeaseTermProps = {
  startDate: string;
  endDate: string;
  comboboxLeaseTermData: {
    value: string;
    meta: Record<string, unknown>;
    calls: number;
  };
  formError: Record<string, string>;
  isEndDateRequired: boolean;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onLeaseTermChange: (value: string, meta: Record<string, unknown>) => void;
  onFormErrorChange: (errors: Record<string, string>) => void;
};

const FormFieldLeaseTerm = ({
  startDate,
  endDate,
  comboboxLeaseTermData,
  formError,
  isEndDateRequired,
  onStartDateChange,
  onEndDateChange,
  onLeaseTermChange,
  onFormErrorChange,
}: FormFieldLeaseTermProps) => {
  const validateStartDate = () => {
    const startDateMs = getMsFromDate(startDate);
    const endDateMs = getMsFromDate(endDate);

    const isStartDateNull = startDateMs === null;
    const isMonthToMonthLeaseTerm = comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[1].value;
    const isStartDateAfterEndDate = startDateMs !== null && endDateMs !== null && startDateMs > endDateMs;
    const isStartDateTheSameAsEndDate = startDateMs !== null && endDateMs !== null && startDateMs === endDateMs;

    const hasErrorStartDate =
      isMonthToMonthLeaseTerm === false && (isStartDateAfterEndDate || isStartDateTheSameAsEndDate || isStartDateNull);
    let errorMessageStartDate = '';

    if (hasErrorStartDate) {
      if (isStartDateNull) {
        errorMessageStartDate = FORM_ERROR_MESSAGES.DATE_REQUIRED;
      }

      if (isStartDateAfterEndDate) {
        errorMessageStartDate = FORM_ERROR_MESSAGES.startDate;
      }

      if (isStartDateTheSameAsEndDate) {
        errorMessageStartDate = FORM_ERROR_MESSAGES.startDateTheSameAsEndDate;
      }
    }

    return { hasErrorStartDate, errorMessageStartDate };
  };

  const validateEndDate = () => {
    const startDateMs = getMsFromDate(startDate);
    const endDateMs = getMsFromDate(endDate);

    const isMonthToMonthLeaseTerm = comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[1].value;
    const isEndDateBeforeStartDate = endDateMs !== null && startDateMs !== null && endDateMs < startDateMs;
    const isEndDateTheSameAsStartDate = endDateMs !== null && startDateMs !== null && endDateMs === startDateMs;

    const hasErrorEndDate =
      isMonthToMonthLeaseTerm === false && (isEndDateBeforeStartDate || isEndDateTheSameAsStartDate);
    let errorMessageEndDate = '';

    if (hasErrorEndDate) {
      // end date can be empty

      if (isEndDateBeforeStartDate) {
        errorMessageEndDate = FORM_ERROR_MESSAGES.endDate;
      }

      if (isEndDateTheSameAsStartDate) {
        errorMessageEndDate = FORM_ERROR_MESSAGES.endDateTheSameAsStartDate;
      }
    }

    return { hasErrorEndDate, errorMessageEndDate };
  };

  // oxlint-disable react-hooks/exhaustive-deps -- validateStartDate/validateEndDate close over
  // startDate/endDate/comboboxLeaseTermData.value; oxlint can't see through the indirection, but these are
  // the real deps that should retrigger validation (formError is intentionally excluded to avoid a loop)
  const validateForm = useCallback(() => {
    const { hasErrorStartDate, errorMessageStartDate } = validateStartDate();
    const { hasErrorEndDate, errorMessageEndDate } = validateEndDate();

    onFormErrorChange({
      ...formError,
      startDate: hasErrorStartDate ? errorMessageStartDate : '',
      endDate: hasErrorEndDate ? errorMessageEndDate : '',
    });
  }, [startDate, endDate, comboboxLeaseTermData.value, onFormErrorChange]);
  // oxlint-enable react-hooks/exhaustive-deps

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const handleCalendarStartChange = useCallback(
    (value: Record<string, Date | string>, _meta?: { reason: string }) => {
      onStartDateChange(value.formatted as string);
    },
    [onStartDateChange],
  );

  const handleCalendarEndDateChange = useCallback(
    (value: Record<string, Date | string>, _meta: { reason: string }) => {
      onEndDateChange(value.formatted as string);
    },
    [onEndDateChange],
  );

  const handleComboboxLeaseTermChange = useCallback(
    (value: string, meta: Record<string, unknown>) => {
      onLeaseTermChange(value, meta);
    },
    [onLeaseTermChange],
  );

  return (
    <div data-testid="FormFieldLeaseTerm">
      <FormField
        data-testid="LeaseTerm"
        label={<Label>Lease term</Label>}
        control={
          <DropdownSelect
            defaultValue={LEASE_TERM_OPTIONS[0].value}
            value={comboboxLeaseTermData.value}
            options={LEASE_TERM_OPTIONS}
            fluidDropdown={true}
            isDropdownPortal={true}
            // @ts-ignore
            onChange={handleComboboxLeaseTermChange}
          />
        }
      />

      <Box className={flexContainerStyles}>
        <Box className={flexRowStyles}>
          <FormField
            data-testid="StartDate"
            error={startDate !== '' && !!formError?.startDate}
            label={<Label required={true}>{FORM_LEASE_TERMS_CONTENT.FORM_DATES_LABEL_MOVE_IN_DATE}</Label>}
            control={
              <DatePicker
                calendar={
                  <Calendar
                    maxDate={endDate ? minusDays(new Date(endDate), 1) : undefined}
                    onSelectedChange={handleCalendarStartChange}
                  />
                }
                closeModalOnDateSelect={true}
                dateFormat="MM/dd/yyyy"
                error={startDate !== '' && !!formError?.startDate}
                input={
                  <DateInput
                    css={{
                      paddingLeft: '400 !important',
                    }}
                    readOnly={true}
                  />
                }
                value={startDate}
              />
            }
            description={
              startDate !== '' && !!formError?.startDate === true ? <FormHelp>{formError.startDate}</FormHelp> : null
            }
          />
        </Box>

        {comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value && (
          <Box className={flexRowStyles}>
            <FormField
              data-testid="EndDate"
              error={endDate !== '' && !!formError?.endDate}
              label={
                <Label required={isEndDateRequired}>{FORM_LEASE_TERMS_CONTENT.FORM_DATES_LABEL_MOVE_OUT_DATE}</Label>
              }
              control={
                <DatePicker
                  closeModalOnDateSelect={true}
                  dateFormat="MM/dd/yyyy"
                  error={endDate !== '' && !!formError?.endDate}
                  value={endDate}
                  calendar={
                    <Calendar
                      minDate={startDate ? addDays(new Date(startDate), 1) : undefined}
                      onSelectedChange={handleCalendarEndDateChange}
                    />
                  }
                  input={
                    <DateInput
                      css={{
                        paddingLeft: '400 !important',
                      }}
                      readOnly={true}
                    />
                  }
                />
              }
              description={
                endDate !== '' && !!formError?.endDate === true ? <FormHelp>{formError.endDate}</FormHelp> : null
              }
            />
          </Box>
        )}
      </Box>
    </div>
  );
};

export default FormFieldLeaseTerm;
